import React from 'react'
import { matchPath, NavLink, useLocation } from 'react-router-dom'
import { LoadPageSeparator, Skeleton, TimeAgo } from '@sketch/components'
import { ReactComponent as CheckmarkIcon } from '@sketch/icons/checkmark-16'
import { ReactComponent as ChatBubbleIcon } from '@sketch/icons/chat-bubble-16'
import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-16'
import { routes } from '@sketch/modules-common'

import { ListHeader, ListWrapper } from './Inbox.styles'
import {
  InboxListItem,
  InboxListItemProps,
} from '../../components/InboxListItem'
import { InboxItem, useInboxNotifications } from './useInboxNotifications'

const skeletonItem: InboxListItemProps = {
  state: 'read',
  avatar: <Skeleton width="100%" height="100%" />,
  title: <Skeleton width="80%" height="14px" />,
  subtitle: <Skeleton width="50%" height="13px" />,
}

const SKELETON_COUNT = 3
const LOAD_MORE_SKELETON_COUNT = 3

const getTypeIcon = (typename: string | undefined) => {
  switch (typename) {
    case 'CommentNotification':
    case 'MentionNotification':
      return <ChatBubbleIcon />
    case 'AddShareMemberNotification':
      return <CheckmarkIcon />
    case 'DownloadableAssetRenderedNotification':
      return <DownloadIcon />
    default:
      return undefined
  }
}

const PlaceholderAvatar = ({
  background = '#d1d1d1',
}: {
  background?: string
}) => (
  <div
    style={{
      width: 40,
      height: 40,
      borderRadius: 8,
      background,
    }}
  />
)

const toListItemProps = (item: InboxItem): InboxListItemProps => ({
  state: item.state,
  avatar: <PlaceholderAvatar />,
  typeIcon: getTypeIcon(item.typename),
  title: item.title,
  subtitle: item.subtitle,
  timeago: <TimeAgo short date={item.createdAt} />,
})

export const InboxList = () => {
  const location = useLocation()
  const { loading, error, groups, loadMore, after } = useInboxNotifications()

  const itemMatch = matchPath<{ notificationId: string }>(location.pathname, {
    path: routes.UPDATES_ITEM.template(),
    exact: true,
  })
  const activeNotificationId = itemMatch?.params?.notificationId || null

  if (loading) {
    return (
      <ListWrapper>
        <ListHeader>Loading…</ListHeader>
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <InboxListItem key={index} {...skeletonItem} />
        ))}
      </ListWrapper>
    )
  }

  if (error) {
    return (
      <ListWrapper>
        <ListHeader>Couldn’t load notifications</ListHeader>
      </ListWrapper>
    )
  }

  if (groups.length === 0) {
    return (
      <ListWrapper>
        <ListHeader>No notifications</ListHeader>
      </ListWrapper>
    )
  }

  return (
    <ListWrapper>
      {groups.map(group => (
        <React.Fragment key={group.id}>
          <ListHeader>{group.label}</ListHeader>
          {group.items.map(item => (
            <NavLink
              key={item.id}
              to={
                item.id === activeNotificationId
                  ? routes.UPDATES.create({})
                  : routes.UPDATES_ITEM.create({ notificationId: item.id })
              }
            >
              <InboxListItem
                {...toListItemProps(item)}
                active={item.id === activeNotificationId}
              />
            </NavLink>
          ))}
        </React.Fragment>
      ))}

      {after && (
        <>
          <LoadPageSeparator key={after} loadNewPage={loadMore} />
          {Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map((_, index) => (
            <InboxListItem key={`load-more-${index}`} {...skeletonItem} />
          ))}
        </>
      )}
    </ListWrapper>
  )
}
